import React, { useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import {
  EnrollmentApi,
  EnrollmentResult,
  TwoFactorMethod,
  TwoFactorSetup
} from "../../services/twoFactorService"

const inputStyle =
  "w-full rounded-lg border border-secondary4 px-4 py-2.5 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"
const primaryButton =
  "rounded-lg bg-dantzOrange px-6 py-2.5 font-medium text-white transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzBlue"
const secondaryButton =
  "rounded-lg px-4 py-2.5 text-sm text-secondary4 transition hover:text-dantzBlue"

interface Props {
  api: EnrollmentApi
  // The account may only use the authenticator app
  appOnly: boolean
  onDone: (result: EnrollmentResult & Record<string, any>) => void
  onCancel?: () => void
  // Called when the login has run out and has to be started again
  onExpired?: () => void
}

// Any app that reads the standard codes works. A few common ones, so nobody is
// left guessing what to install.
const APP_EXAMPLES = [
  "Passwords (built into iPhone, iOS 18 or later)",
  "2FAS (iPhone and Android)",
  "Ente Auth (iPhone and Android)",
  "Bitwarden Authenticator (iPhone and Android)",
  "Aegis (Android)",
  "Google Authenticator or Microsoft Authenticator"
]

const options: Array<{ method: TwoFactorMethod; title: string; text: string }> = [
  {
    method: "app",
    title: "Authenticator app (recommended)",
    text: "A 6-digit code from an app on your phone. The most secure choice."
  },
  {
    method: "email",
    title: "Code by e-mail",
    text: "A 6-digit code sent to your e-mail address each time you log in. No extra app needed."
  }
]

// Choose a method, start it, and confirm it with the first code
export default function TwoFactorEnroll({ api, appOnly, onDone, onCancel, onExpired }: Props) {
  const [method, setMethod] = useState<TwoFactorMethod>("app")
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null)
  const [code, setCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [info, setInfo] = useState("")

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true)
    setError("")
    try {
      await action()
    } catch (err: any) {
      if (err?.expired && onExpired) onExpired()
      setError(err?.message || fallback)
    } finally {
      setBusy(false)
    }
  }

  const start = () =>
    run(async () => {
      setSetup(await api.start(method))
      setInfo("")
    }, "The setup could not be started. Please try again.")

  const resend = () =>
    run(async () => {
      await api.start("email")
      setInfo("A new code was sent.")
    }, "The code could not be sent. Please try again.")

  const finish = (e: React.FormEvent) => {
    e.preventDefault()
    return run(async () => {
      onDone(await api.finish(method, code.trim()))
    }, "Two-factor authentication could not be turned on. Please try again.")
  }

  const visible = appOnly ? options.filter((option) => option.method === "app") : options

  return (
    <div className="flex flex-col gap-4">
      {!setup && (
        <>
          {appOnly && (
            <p className="text-sm text-gray-600">
              Your account needs an authenticator app on your phone.
            </p>
          )}

          <div className="flex flex-col gap-2" role="radiogroup" aria-label="Method">
            {visible.map((option) => (
              <label
                key={option.method}
                className={`flex cursor-pointer gap-3 rounded-lg border px-4 py-3 text-sm transition ${
                  method === option.method
                    ? "border-dantzOrange bg-dantzOrange/5"
                    : "border-gray-200 hover:border-secondary4"
                }`}
              >
                <input
                  type="radio"
                  name="two-factor-method"
                  checked={method === option.method}
                  onChange={() => setMethod(option.method)}
                  className="mt-1 accent-dantzOrange"
                />
                <span>
                  <span className="block font-medium text-dantzBlue">{option.title}</span>
                  <span className="block text-gray-600">{option.text}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={start} disabled={busy} className={primaryButton}>
              {busy ? <SpinnerText>Starting…</SpinnerText> : "Continue"}
            </button>
            {onCancel && (
              <button type="button" onClick={onCancel} className={secondaryButton}>
                Cancel
              </button>
            )}
          </div>
        </>
      )}

      {setup && (
        <form onSubmit={finish} className="flex flex-col gap-4">
          {setup.method === "app" ? (
            <>
              <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600">
                <p className="font-medium text-dantzBlue">
                  Any authenticator app works. For example:
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {APP_EXAMPLES.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
                {!appOnly && (
                  <p className="mt-2">
                    No app on your phone? Go back and choose the code by
                    e-mail instead.
                  </p>
                )}
              </div>

              <ol className="list-decimal pl-5 text-sm text-gray-600">
                <li>Open an authenticator app on your phone.</li>
                <li>Scan this QR code.</li>
                <li>Enter the 6-digit code the app shows.</li>
              </ol>

              <img
                src={setup.qrCodeDataUrl}
                alt="QR code for your authenticator app"
                width={220}
                height={220}
                className="self-center rounded-lg border border-gray-200"
              />

              <p className="text-center text-xs text-gray-500">
                Cannot scan it? Enter this key in the app instead:
                <br />
                <span className="select-all font-mono text-sm tracking-wider text-gray-800">
                  {setup.secret}
                </span>
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-600">
              We sent a 6-digit code to {setup.sentTo}. It is valid for 10
              minutes.
            </p>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              6-digit code *
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={7}
              value={code}
              onChange={(e) => {
                setCode(e.target.value)
                setError("")
              }}
              className={inputStyle}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={busy || !code.trim()} className={primaryButton}>
              {busy ? <SpinnerText>Checking…</SpinnerText> : "Turn on"}
            </button>
            {setup.method === "email" && (
              <button type="button" onClick={resend} disabled={busy} className={secondaryButton}>
                Send a new code
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setSetup(null)
                setCode("")
                setError("")
                setInfo("")
              }}
              className={secondaryButton}
            >
              Back
            </button>
          </div>
        </form>
      )}

      {info && (
        <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">{info}</div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
      )}
    </div>
  )
}
